'use client';

import { Button, DialogFooter, FormField, Label, RadioGroup, RadioGroupItem, ScrollArea } from '@shared/components/shadcn';
import { Spinner } from '@shared/icons';
import { Affordability } from '@shared/icons/system';
import { useTranslations } from 'next-intl';
import { useFormContext } from 'react-hook-form';
import { FieldMessage } from '../../../../form-controls/FieldMessage/FieldMessage';
import { useMultistepForm } from '../../../../form-controls/MultistepForm/hooks/useMultistepForm';
import type { QuestionnaireStepProps } from '../types';

const STATEMENT_DATA_TRACK_VALUES = ['Affordable', 'Unaffordable'] as const;

const StatementStep = ({ question }: QuestionnaireStepProps) => {
  const t = useTranslations('global_dialogs.affordability_questionnaire');
  const tForm = useTranslations('form');
  const { goToNextStep, isLoading } = useMultistepForm();
  const { control, watch } = useFormContext();

  const selectedValue = watch('statement');

  const options = question.options.map((optionKey, index) => ({
    value: optionKey,
    label: t(optionKey),
    dataTrackValue: STATEMENT_DATA_TRACK_VALUES[index],
  }));

  const selectedOption = options.find(option => option.value === selectedValue);

  return (
    <div className="flex min-h-0 flex-1 flex-col pt-10 md:pt-6">
      <ScrollArea className="flex min-h-0 flex-1 flex-col gap-4">
        <Affordability size="extra-extra-large" data-testid="affordability-questionnaire-icon" />
        <h4 className="mt-3">{t(question.label)}</h4>
        <div id="statement-field" className="flex flex-col gap-4 mt-6">
          <FormField
            control={control}
            name="statement"
            render={({ field, fieldState }) => (
              <div ref={field.ref} className="flex flex-col gap-4">
                <RadioGroup value={field.value} onValueChange={field.onChange} className="flex flex-col gap-4 w-full" data-track-name="Statement">
                  {options.map(option => (
                    <div key={option.value} className="flex flex-row gap-3 h-10 items-center justify-start w-full" data-track-value={option.dataTrackValue}>
                      <RadioGroupItem
                        className="size-7"
                        value={option.value}
                        id={`statement-${option.value}`}
                        data-testid={`statement-${option.value}`}
                        data-track-value={option.dataTrackValue}
                      />
                      <Label className="paragraph-explanatory hover:text-inherit" htmlFor={`statement-${option.value}`}>
                        {option.label}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
                <FieldMessage message={fieldState.error?.message} messageType="error" data-track-helper="Statement" />
              </div>
            )}
          />
        </div>
      </ScrollArea>
      <DialogFooter data-track-container="Actions">
        <Button onClick={goToNextStep} disabled={isLoading} data-testid="continue-button" data-track-name="Continue" data-track-value={selectedOption?.dataTrackValue}>
          {isLoading ? <Spinner size="small" className="animate-spin w-5" aria-label={tForm('aria.loading')} /> : t('continue')}
        </Button>
      </DialogFooter>
    </div>
  );
};

export { StatementStep };
